import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DashboardLayout } from './Dashboard';
import { ArrowLeft, ArrowsClockwise, Warning, CheckCircle, Calendar, FileText, Lightning } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SnapshotChangesView = ({ changes }) => {
  const { t } = useTranslation();
  if (!changes || Object.keys(changes).length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#10B981] mt-2">
        <CheckCircle size={14} weight="fill" />
        {t('mon.no_changes')}
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2 p-3 bg-[#0A0A0A] border border-[#F59E0B]/30 rounded-lg">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F59E0B] font-medium">
        <Lightning size={14} weight="fill" />
        {t('mon.changes_det')}
      </div>
      {Object.entries(changes).map(([field, diff]) => (
        <div key={field} className="text-sm">
          <p className="text-white/80 font-medium capitalize">{field}:</p>
          {diff.old !== undefined && (
            <p className="text-xs text-[#EF4444] break-all">- {String(diff.old).substring(0, 200)}</p>
          )}
          {diff.new !== undefined && (
            <p className="text-xs text-[#10B981] break-all">+ {String(diff.new).substring(0, 200)}</p>
          )}
          {diff.added?.length > 0 && (
            <p className="text-xs text-[#10B981]">+ {t('mon.added')} {diff.added.join(', ')}</p>
          )}
          {diff.removed?.length > 0 && (
            <p className="text-xs text-[#EF4444]">- {t('mon.removed_w')} {diff.removed.join(', ')}</p>
          )}
        </div>
      ))}
    </div>
  );
};

const SnapshotTimelineItem = ({ snapshot, isLast, isFirst }) => {
  const { t, i18n } = useTranslation();
  const hasChanges = snapshot.changes && Object.keys(snapshot.changes).length > 0;
  const dotColor = isFirst ? '#10B981' : hasChanges ? '#F59E0B' : '#2563EB';

  return (
    <div className="relative pl-12 pb-6">
      {/* Vertical line */}
      {!isLast && (
        <div className="absolute left-[19px] top-6 bottom-0 w-px bg-white/10"></div>
      )}
      {/* Dot */}
      <div
        className="absolute left-2 top-1 w-6 h-6 rounded-full border-4 border-[#050505]"
        style={{ backgroundColor: dotColor }}
      ></div>
      {/* Content */}
      <div className="p-4 bg-[#121212] border border-white/10 rounded-xl">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Calendar size={14} weight="bold" />
            {new Date(snapshot.created_at).toLocaleString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}
          </div>
          {isFirst && (
            <span className="px-2 py-0.5 bg-[#10B981]/20 text-[#10B981] text-xs rounded-full font-medium">
              {t('mon.most_recent')}
            </span>
          )}
          {hasChanges && !isFirst && (
            <span className="px-2 py-0.5 bg-[#F59E0B]/20 text-[#F59E0B] text-xs rounded-full font-medium flex items-center gap-1">
              <Warning size={12} weight="bold" />
              {t('mon.change')}
            </span>
          )}
        </div>
        <h4 className="font-medium mb-1 truncate">{snapshot.title || t('mon.no_title')}</h4>
        {snapshot.description && (
          <p className="text-sm text-white/60 line-clamp-2">{snapshot.description}</p>
        )}
        {snapshot.h1_tags?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {snapshot.h1_tags.slice(0, 3).map((h1, idx) => (
              <span
                key={`${snapshot.snapshot_id}-h1-${idx}`}
                className="px-2 py-0.5 bg-[#0A0A0A] border border-white/10 rounded text-xs text-white/60 truncate max-w-xs"
              >
                {h1}
              </span>
            ))}
          </div>
        )}
        <SnapshotChangesView changes={snapshot.changes} />
      </div>
    </div>
  );
};

const MonitorStats = ({ monitor, snapshotCount, totalChanges }) => {
  const { t } = useTranslation();
  return (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
    <div className="p-4 bg-[#121212] border border-white/10 rounded-xl">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">Snapshots</p>
      <p className="text-3xl font-[Outfit] font-bold">{snapshotCount}</p>
    </div>
    <div className="p-4 bg-[#121212] border border-white/10 rounded-xl">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">{t('mon.total_changes')}</p>
      <p className="text-3xl font-[Outfit] font-bold text-[#F59E0B]">{totalChanges}</p>
    </div>
    <div className="p-4 bg-[#121212] border border-white/10 rounded-xl">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">{t('mon.status')}</p>
      <p className="text-lg font-[Outfit] font-bold flex items-center gap-2">
        {monitor.has_changes ? (
          <>
            <Warning size={20} weight="fill" className="text-[#F59E0B]" />
            <span className="text-[#F59E0B]">{t('mon.recent_changes')}</span>
          </>
        ) : (
          <>
            <CheckCircle size={20} weight="fill" className="text-[#10B981]" />
            <span className="text-[#10B981]">{t('mon.stable')}</span>
          </>
        )}
      </p>
    </div>
  </div>
  );
};

const MonitorDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitor/${id}`, { withCredentials: true });
      setData(response.data);
    } catch {
      navigate('/monitoring');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleCheck = useCallback(async () => {
    setChecking(true);
    try {
      await axios.post(`${BACKEND_URL}/api/monitor/${id}/check`, {}, { withCredentials: true });
      await loadDetail();
    } catch {
      // Silently fail
    } finally {
      setChecking(false);
    }
  }, [id, loadDetail]);

  if (loading) {
    return (
      <DashboardLayout user={user} activePage="/monitoring">
        <div className="flex items-center justify-center h-full">
          <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) return null;

  const { monitor, snapshots, snapshot_count, total_changes_detected } = data;

  return (
    <DashboardLayout user={user} activePage="/monitoring">
      <div className="p-6" data-testid="monitor-detail-page">
        {/* Back + Header */}
        <button
          onClick={() => navigate('/monitoring')}
          data-testid="back-to-monitors"
          className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={18} weight="bold" />
          {t('mon.back_monitors')}
        </button>

        <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-[Outfit] font-bold tracking-tight mb-2 truncate">
              {monitor.label}
            </h1>
            <a
              href={monitor.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#2563EB] hover:text-[#3B82F6] break-all"
            >
              {monitor.url}
            </a>
          </div>
          <button
            onClick={handleCheck}
            data-testid="trigger-check-btn"
            disabled={checking}
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium transition-all disabled:opacity-50"
          >
            <ArrowsClockwise size={18} weight="bold" className={checking ? 'animate-spin' : ''} />
            {checking ? t('mon.checking') : t('mon.check_now')}
          </button>
        </div>

        <MonitorStats
          monitor={monitor}
          snapshotCount={snapshot_count}
          totalChanges={total_changes_detected}
        />

        {/* Timeline */}
        <div className="bg-[#121212] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <FileText size={20} weight="bold" className="text-[#2563EB]" />
            <h2 className="text-xl font-[Outfit] font-bold">{t('mon.timeline')}</h2>
            <span className="text-sm text-white/40 ml-2">({snapshots.length} snapshots)</span>
          </div>

          {snapshots.length === 0 ? (
            <p className="text-white/40 text-center py-8">{t('mon.no_snapshots')}</p>
          ) : (
            <div className="relative">
              {snapshots.map((snapshot, idx) => (
                <SnapshotTimelineItem
                  key={snapshot.snapshot_id}
                  snapshot={snapshot}
                  isFirst={idx === 0}
                  isLast={idx === snapshots.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MonitorDetail;
